import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Lightning, Globe, Brain, Rocket, UsersThree, ChartLineUp, Trophy } from '@phosphor-icons/react';

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const team = [
  { name: 'Alex Mercer', role: 'Lead Architect', image: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=200&h=200&fit=crop&crop=face', bio: 'Former senior engineer at Coinbase. 10+ years in blockchain and MEV research.' },
  { name: 'Sarah Chen', role: 'Quant Engineer', image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=200&h=200&fit=crop&crop=face', bio: 'PhD in Mathematics. Built HFT systems at Jump Trading before joining DeFiBot.' },
  { name: 'David Kross', role: 'Security Head', image: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=200&h=200&fit=crop&crop=face', bio: 'Former security researcher at OpenZeppelin. Audited 100+ smart contracts.' },
  { name: 'Elena Rostova', role: 'DeFi Strategist', image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=200&h=200&fit=crop&crop=face', bio: 'DeFi native since 2019. Led product at Aave and Uniswap Labs.' },
];

const values = [
  { icon: ShieldCheck, title: 'Security First', desc: 'Every transaction protected by Flashbots relay and anti-MEV shields.' },
  { icon: Brain, title: 'AI-Driven', desc: 'Neural networks analyze mempools 24/7 to find optimal execution paths.' },
  { icon: Lightning, title: 'Speed Matters', desc: 'Sub-200ms execution through proprietary low-latency infrastructure.' },
  { icon: Globe, title: 'Multi-Chain', desc: '7 EVM networks, hundreds of DEXes, one unified terminal.' },
];

const About = () => (
  <div className="min-h-screen pt-24 pb-16" data-testid="about-page">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <motion.div initial="hidden" animate="visible" variants={stagger} className="text-center mb-20">
        <motion.span variants={fadeUp} className="text-sm uppercase tracking-[0.2em] text-[var(--color-primary)] font-semibold">About Us</motion.span>
        <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl font-bold text-[var(--text-primary)] mt-4 mb-6" style={{ fontFamily: "'Unbounded', sans-serif" }}>
          Building the Future of<br /><span className="bg-gradient-to-r from-[#00E5FF] to-[#0047FF] bg-clip-text text-transparent">Automated Trading</span>
        </motion.h1>
        <motion.p variants={fadeUp} className="text-lg text-[var(--text-secondary)] max-w-3xl mx-auto leading-relaxed">
          DeFiBot started in 2021 with a simple mission: make institutional-grade MEV extraction accessible to every trader. Today, our AI-powered terminal processes millions of transactions across 7 EVM networks, helping 50,000+ traders maximize their returns.
        </motion.p>
      </motion.div>

      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20">
        {[{ n: '50K+', l: 'Active Traders' }, { n: '$847M+', l: 'Total Volume' }, { n: '94.7%', l: 'Success Rate' }, { n: '7', l: 'Networks' }].map((s, i) => (
          <motion.div key={i} variants={fadeUp} className="p-6 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)] text-center">
            <div className="text-3xl font-bold text-[var(--color-primary)]" style={{ fontFamily: 'JetBrains Mono' }}>{s.n}</div>
            <div className="text-sm text-[var(--text-secondary)] mt-1">{s.l}</div>
          </motion.div>
        ))}
      </motion.div>

      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="mb-20">
        <motion.h2 variants={fadeUp} className="text-2xl font-bold text-[var(--text-primary)] mb-8 text-center" style={{ fontFamily: "'Unbounded', sans-serif" }}>Our Values</motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {values.map((v, i) => (
            <motion.div key={i} variants={fadeUp} whileHover={{ y: -4 }} className="p-6 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)] hover:border-[var(--color-primary)]/30 transition-all">
              <div className="w-12 h-12 rounded-xl bg-[var(--color-primary)]/10 flex items-center justify-center mb-4"><v.icon size={24} className="text-[var(--color-primary)]" weight="bold" /></div>
              <h3 className="text-lg font-bold text-[var(--text-primary)] mb-2">{v.title}</h3>
              <p className="text-sm text-[var(--text-secondary)]">{v.desc}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
        <motion.h2 variants={fadeUp} className="text-2xl font-bold text-[var(--text-primary)] mb-8 text-center" style={{ fontFamily: "'Unbounded', sans-serif" }}>Meet the Team</motion.h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {team.map((m, i) => (
            <motion.div key={i} variants={fadeUp} whileHover={{ y: -4 }} className="p-6 rounded-2xl bg-[var(--bg-surface-1)] border border-[var(--border-default)] text-center hover:border-[var(--color-primary)]/30 transition-all" data-testid={`team-member-${i}`}>
              <img src={m.image} alt={m.name} className="w-20 h-20 rounded-full mx-auto mb-4 object-cover border-2 border-[var(--color-primary)]/30" />
              <h3 className="text-base font-bold text-[var(--text-primary)]">{m.name}</h3>
              <p className="text-[var(--color-primary)] text-sm font-medium mb-2">{m.role}</p>
              <p className="text-[var(--text-secondary)] text-xs">{m.bio}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>
    </div>
  </div>
);

export default About;
